import React, { useState } from "react";
import { FormattedMessage, defineMessages } from "react-intl";
import styled from "styled-components";
import { APP_COLORS } from "../../Common/constants";

const Title = styled.div`
  color: ${APP_COLORS.primary};
  font-weight: bold;
  font-size: 20px;
  margin-bottom: 18px;
`;

const Paragraph = styled.p`
  color: ${APP_COLORS["gray-darkish"]};
  font-size: 15px;
  line-height: 1.5;
  margin: 0 0 14px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const SeeMoreButton = styled.button`
  padding: 0;
  border: none;
  background: none;
  color: ${APP_COLORS.primary};
  font-size: 15px;
  text-decoration: underline;
  cursor: pointer;
`;

const MotorList = styled.div`
  margin-top: 6px;
  color: ${APP_COLORS["gray-darkish"]};
  font-weight: bold;
  font-size: 15px;
  overflow-wrap: anywhere;
`;

const bold = chunks => <strong>{chunks}</strong>;

export const preferredModalMessages = defineMessages({
  // Aggregator marks a template as the default (3 actions).
  forcePreferredTitle: {
    id: "emailConfiguration.form.forcePreferredTitle",
    description: "Forced preferred modal title",
    defaultMessage: "Preferred template",
  },
  forcePreferredText: {
    id: "emailConfiguration.form.forcePreferredText",
    description: "Forced preferred modal text",
    defaultMessage:
      "You marked this template as preferred. How should it be applied to the selected engines?",
  },
  forcePreferredRespectIndividual: {
    id: "emailConfiguration.form.forcePreferredRespectIndividual",
    description: "Forced preferred respect-individual button",
    defaultMessage: "Respect individual template",
  },
  forcePreferredReplaceAll: {
    id: "emailConfiguration.form.forcePreferredReplaceAll",
    description: "Forced preferred replace-for-all button",
    defaultMessage: "Replace for all",
  },

  // Aggregator removes the default (3 actions).
  removePreferredTitle: {
    id: "emailConfiguration.form.removePreferredTitle",
    description: "Title of the modal shown when the aggregator unmarks a default template",
    defaultMessage: "Remove default template",
  },
  removePreferredSingleEngine: {
    id: "emailConfiguration.form.removePreferredSingleEngine",
    description: "Remove-default modal: the template is the default on exactly one engine",
    defaultMessage: "This template is the default on <b>1 engine</b>: <b>{motor}</b>.",
  },
  removePreferredMultipleEngines: {
    id: "emailConfiguration.form.removePreferredMultipleEngines",
    description: "Remove-default modal: the template is the default on several engines",
    defaultMessage: "This template is the default on <b>{count} engines</b>.",
  },
  removePreferredSeeMore: {
    id: "emailConfiguration.form.removePreferredSeeMore",
    description: "Expands the list of engines in the remove-default modal",
    defaultMessage: "See more",
  },
  removePreferredSeeLess: {
    id: "emailConfiguration.form.removePreferredSeeLess",
    description: "Collapses the list of engines in the remove-default modal",
    defaultMessage: "See less",
  },
  removePreferredInfo: {
    id: "emailConfiguration.form.removePreferredInfo",
    description: "Remove-default modal: what happens to engines left without a default",
    defaultMessage:
      "Engines left without a default template will use the standard Witbooking template. Review which template you want to use as the default on the individual engine.",
  },
  removePreferredKeepIndividual: {
    id: "emailConfiguration.form.removePreferredKeepIndividual",
    description: "Keep-on-individual-engines button of the remove-default modal",
    defaultMessage: "Keep on individual engines",
  },
  removePreferredRemoveAll: {
    id: "emailConfiguration.form.removePreferredRemoveAll",
    description: "Remove-for-all button of the remove-default modal",
    defaultMessage: "Remove for all",
  },

  // A template takes over the engine's default, displacing the other origin (2 actions).
  markAsDefaultTitle: {
    id: "emailConfiguration.form.markAsDefaultTitle",
    description: "Title of both default-handover confirmations",
    defaultMessage: "Set as default",
  },
  overrideAgrPreferredText: {
    id: "emailConfiguration.form.overrideAgrPreferredText",
    description: "Confirmation shown before an own template takes over the aggregator's default",
    defaultMessage:
      "If you set this template as the default, <b>the aggregator template will no longer apply</b> on this engine.",
  },
  overrideOwnPreferredText: {
    id: "emailConfiguration.form.overrideOwnPreferredText",
    description: "Confirmation shown before the aggregator's template takes over the engine default",
    defaultMessage:
      "If you set this template as the default, <b>the individual engine's template will no longer apply</b> on this engine.",
  },
  overridePreferredAccept: {
    id: "emailConfiguration.form.overridePreferredAccept",
    description: "Accept button of both default-handover confirmations",
    defaultMessage: "Accept",
  },
});

export function ForcePreferredBody() {
  return (
    <>
      <Title>
        <FormattedMessage {...preferredModalMessages.forcePreferredTitle} />
      </Title>
      <Paragraph>
        <FormattedMessage {...preferredModalMessages.forcePreferredText} />
      </Paragraph>
    </>
  );
}

// `motors` are the selected children that currently hold this template as their default. With
// several, the names collapse behind a "see more" toggle so the modal stays readable.
export function RemovePreferredBody({ motors }) {
  const [isExpanded, setIsExpanded] = useState(false);
  return (
    <>
      <Title>
        <FormattedMessage {...preferredModalMessages.removePreferredTitle} />
      </Title>
      {motors.length === 1 && (
        <Paragraph>
          <FormattedMessage
            {...preferredModalMessages.removePreferredSingleEngine}
            values={{ b: bold, motor: motors[0] }}
          />
        </Paragraph>
      )}
      {motors.length > 1 && (
        <Paragraph>
          <FormattedMessage
            {...preferredModalMessages.removePreferredMultipleEngines}
            values={{ b: bold, count: motors.length }}
          />{" "}
          <SeeMoreButton type="button" onClick={() => setIsExpanded(current => !current)}>
            <FormattedMessage
              {...(isExpanded
                ? preferredModalMessages.removePreferredSeeLess
                : preferredModalMessages.removePreferredSeeMore)}
            />
          </SeeMoreButton>
          {isExpanded && <MotorList>{motors.join(", ")}</MotorList>}
        </Paragraph>
      )}
      <Paragraph>
        <FormattedMessage {...preferredModalMessages.removePreferredInfo} />
      </Paragraph>
    </>
  );
}

export function MarkAsDefaultBody({ message }) {
  return (
    <>
      <Title>
        <FormattedMessage {...preferredModalMessages.markAsDefaultTitle} />
      </Title>
      <Paragraph>
        <FormattedMessage {...message} values={{ b: bold }} />
      </Paragraph>
    </>
  );
}
